
// === AUDIO GATE (REQUIRED FOR CHROME) ===
let audioEnabled = false;

function enableAudio() {
  audioEnabled = true;
  window.removeEventListener("click", enableAudio);
  window.removeEventListener("keydown", enableAudio);
}

window.addEventListener("click", enableAudio);
window.addEventListener("keydown", enableAudio);


// === AUDIO CONTEXT RESUME FIX ===
let audioUnlocked = false;

function unlockAudioContext(ctx) {
  if (audioUnlocked) return;

  const unlock = () => {
    if (ctx.state !== "running") {
      ctx.resume();
    }
    audioUnlocked = true;
    window.removeEventListener("click", unlock);
    window.removeEventListener("keydown", unlock);
  };

  window.addEventListener("click", unlock);
  window.addEventListener("keydown", unlock);
}

import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, BellOff, Volume2, VolumeX, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";

const API = "/api";

// Web Audio API sound generator
class SoundGenerator {
  constructor() {
    this.audioContext = null;
    this.volume = 0.5;
  }

  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  }

  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
  }

  playTone(frequency, duration, type = "sine", attack = 0.01, decay = 0.1) {
    const ctx = this.init();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
    
    // Envelope
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(this.volume, ctx.currentTime + attack);
    gainNode.gain.linearRampToValueAtTime(this.volume * 0.7, ctx.currentTime + attack + decay);
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  }

  // Fibonacci level touch - gentle ping
  playFibTouch(isKeyLevel = false) {
    const ctx = this.init();
    if (isKeyLevel) {
      // Double ping for key levels (61.8%, 50%, 38.2%)
      this.playTone(880, 0.15, "sine", 0.01, 0.05);
      setTimeout(() => this.playTone(1100, 0.15, "sine", 0.01, 0.05), 100);
    } else {
      this.playTone(660, 0.12, "sine", 0.01, 0.05);
    }
  }

  // Long position opened - rising tone
  playLongOpen() {
    const ctx = this.init();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = "sine";
    osc.frequency.setValueAtTime(400, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(800, ctx.currentTime + 0.2);
    
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(this.volume, ctx.currentTime + 0.02);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
    
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  }

  // Short position opened - falling tone
  playShortOpen() {
    const ctx = this.init();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = "sine";
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(400, ctx.currentTime + 0.2);
    
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(this.volume, ctx.currentTime + 0.02);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
    
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  }

  // Stop loss hit - low warning tone
  playStopLoss() {
    this.playTone(200, 0.3, "sawtooth", 0.01, 0.1);
    setTimeout(() => this.playTone(150, 0.3, "sawtooth", 0.01, 0.1), 200);
  }

  // Take profit hit - success chime
  playTakeProfit() {
    this.playTone(523, 0.15, "sine", 0.01, 0.05); // C
    setTimeout(() => this.playTone(659, 0.15, "sine", 0.01, 0.05), 100); // E
    setTimeout(() => this.playTone(784, 0.2, "sine", 0.01, 0.05), 200); // G
  }
}

const soundGen = new SoundGenerator();

export const AudioAlerts = ({ onAlertReceived }) => {
  const [enabled, setEnabled] = useState(true);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(50);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [lastAlertId, setLastAlertId] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState([]);

  // Fetch alert settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/alerts/settings`);
        setSettings(res.data);
      } catch (e) {
        console.error("Error fetching alert settings:", e);
      }
    };
    fetchSettings();
  }, []);

  // Update volume
  useEffect(() => {
    soundGen.setVolume(muted ? 0 : volume / 100);
  }, [volume, muted]);

  // Poll for new alerts
  useEffect(() => {
    if (!enabled) return;

    const pollAlerts = async () => {
      try {
        const url = lastAlertId 
          ? `${API}/alerts/poll?since_id=${lastAlertId}`
          : `${API}/alerts/poll`;
        const res = await axios.get(url);
        
        if (res.data.alerts && res.data.alerts.length > 0) {
          const newAlerts = res.data.alerts;
          setLastAlertId(newAlerts[newAlerts.length - 1].id);
          
          // Process each new alert
          newAlerts.forEach(alert => {
            handleAlert(alert);
          });
          
          // Update recent alerts list
          setRecentAlerts(prev => [...prev, ...newAlerts].slice(-10));
          
          if (onAlertReceived) {
            onAlertReceived(newAlerts);
          }
        }
      } catch (e) {
        console.error("Error polling alerts:", e);
      }
    };

    const interval = setInterval(pollAlerts, 2000);
    return () => clearInterval(interval);
  }, [enabled, lastAlertId, onAlertReceived]);

  const handleAlert = useCallback((alert) => {
    if (muted) return;

    // Play appropriate sound
    switch (alert.type) {
      case "fib_touch":
        soundGen.playFibTouch(alert.data?.is_key_level);
        toast.info(alert.message, { duration: 3000 });
        break;
      case "trade_open":
        if (alert.data?.direction === "long") {
          soundGen.playLongOpen();
        } else {
          soundGen.playShortOpen();
        }
        break;
      case "sl_hit":
        soundGen.playStopLoss();
        toast.error(alert.message, { duration: 4000 });
        break;
      case "tp_hit":
        soundGen.playTakeProfit();
        toast.success(alert.message, { duration: 4000 });
        break;
      default:
        soundGen.playFibTouch(false);
    }
  }, [muted]);

  const updateSetting = async (key, value) => {
    try {
      const params = new URLSearchParams();
      params.append(key, value.toString());
      await axios.post(`${API}/alerts/settings?${params.toString()}`);
      setSettings(prev => ({ ...prev, [key]: value }));
    } catch (e) {
      toast.error("Failed to update setting");
    }
  };

  const testSound = async (type) => {
    try {
      await axios.post(`${API}/alerts/test?alert_type=${type}`);
    } catch (e) {
      console.error("Test alert failed:", e);
    }
  };

  return (
    <div className="relative">
      {/* Main toggle button */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMuted(!muted)}
          className={`hover:bg-zinc-800 ${muted ? 'text-zinc-500' : 'text-fuchsia-500'}`}
          title={muted ? "Unmute alerts" : "Mute alerts"}
          data-testid="mute-toggle"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </Button>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowSettings(!showSettings)}
          className={`hover:bg-zinc-800 ${showSettings ? 'bg-zinc-800' : ''}`}
          title="Alert settings"
          data-testid="alert-settings-btn"
        >
          <Bell className="w-4 h-4" />
        </Button>
      </div>

      {/* Settings dropdown */}
      {showSettings && (
        <div className="absolute top-10 right-0 w-72 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl p-4 space-y-4" style={{ zIndex: 9999 }} data-testid="alert-settings-panel">
          <div className="flex items-center justify-between pb-2 border-b border-zinc-800">
            <span className="text-sm font-medium">Audio Alerts</span>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="alerts-enabled-toggle"
            />
          </div>

          {/* Volume slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">Volume</span>
              <span className="text-xs text-zinc-400 font-mono">{volume}%</span>
            </div>
            <Slider
              value={[volume]}
              onValueChange={([v]) => setVolume(v)}
              min={0}
              max={100}
              step={5}
              disabled={muted}
            />
          </div>

          {/* Alert type toggles */}
          {settings && (
            <div className="space-y-3 pt-2 border-t border-zinc-800">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">Fib Level Touch</span>
                <Switch
                  checked={settings.fib_touch_enabled}
                  onCheckedChange={(v) => updateSetting("fib_touch_enabled", v)}
                  data-testid="fib-touch-toggle"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">Trade Open</span>
                <Switch
                  checked={settings.trade_open_enabled}
                  onCheckedChange={(v) => updateSetting("trade_open_enabled", v)}
                  data-testid="trade-open-toggle"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">SL/TP Hit</span>
                <Switch
                  checked={settings.sl_tp_hit_enabled}
                  onCheckedChange={(v) => updateSetting("sl_tp_hit_enabled", v)}
                  data-testid="sl-tp-toggle"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">Key Levels Only</span>
                <Switch
                  checked={settings.key_levels_only}
                  onCheckedChange={(v) => updateSetting("key_levels_only", v)}
                  data-testid="key-levels-toggle"
                />
              </div>
            </div>
          )}

          {/* Test sounds */}
          <div className="space-y-2 pt-2 border-t border-zinc-800">
            <span className="text-xs text-zinc-500">Test Sounds</span>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => testSound("fib_touch")}
                className="px-2 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                data-testid="test-fib-sound"
              >
                Fib Touch
              </button>
              <button
                onClick={() => testSound("long_open")}
                className="px-2 py-1.5 text-xs bg-green-900/30 text-green-500 hover:bg-green-900/50 rounded transition-colors"
                data-testid="test-long-sound"
              >
                Long Open
              </button>
              <button
                onClick={() => testSound("short_open")}
                className="px-2 py-1.5 text-xs bg-red-900/30 text-red-500 hover:bg-red-900/50 rounded transition-colors"
                data-testid="test-short-sound"
              >
                Short Open
              </button>
              <button
                onClick={() => testSound("tp_hit")}
                className="px-2 py-1.5 text-xs bg-fuchsia-900/30 text-fuchsia-500 hover:bg-fuchsia-900/50 rounded transition-colors"
                data-testid="test-tp-sound"
              >
                TP Hit
              </button>
            </div>
          </div>

          {/* Recent alerts */}
          {recentAlerts.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-zinc-800">
              <span className="text-xs text-zinc-500">Recent Alerts</span>
              <div className="max-h-24 overflow-y-auto space-y-1">
                {recentAlerts.slice(-5).reverse().map((alert) => (
                  <div key={alert.id} className="text-xs text-zinc-400 p-1.5 bg-zinc-800/50 rounded truncate">
                    {alert.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
